import React, { useId, useRef, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSliceBalance } from '../hooks/useSliceBalance';
import { notificationService } from '../services/notificationService';
import {
  Home,
  LayoutGrid,
  Search,
  Film,
  Tv,
  Sparkles,
  Pizza,
  User as UserIcon,
  LogOut,
  Bookmark,
  History,
  Bell,
  ChevronRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useViewerNotifications } from '../hooks/useViewerNotifications';

export const KinoraMark = ({ size = 28, glow = false }) => {
  const id = useId();
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      aria-hidden="true"
      className="shrink-0"
    >
      <defs>
        <linearGradient id={`${id}-g`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="oklch(0.86 0.10 265)" />
          <stop offset="100%" stopColor="oklch(0.72 0.14 285)" />
        </linearGradient>
        {glow && (
          <filter id={`${id}-glow`} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="4" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        )}
      </defs>
      <circle
        cx="50"
        cy="50"
        r="42"
        fill="none"
        stroke="hsl(var(--primary))"
        strokeOpacity="0.25"
        strokeWidth="3"
      />
      <path
        d="M 50 50 L 50 8 A 42 42 0 0 1 86.37 71 Z"
        fill={`url(#${id}-g)`}
        filter={glow ? `url(#${id}-glow)` : undefined}
        strokeLinejoin="round"
      />
      <circle cx="50" cy="50" r="3.5" fill="hsl(var(--background))" />
    </svg>
  );
};

const NAV = [
  { id: 'home', label: 'Home', icon: Home, to: '/home' },
  { id: 'browse', label: 'Browse', icon: LayoutGrid, to: '/browse' },
  { id: 'search', label: 'Search', icon: Search, action: 'search' },
  { id: 'crowdfund', label: 'Crowdfund', icon: Sparkles, to: '/crowdfund' },
  { id: 'fund', label: 'Fund', icon: Pizza, to: '/fund' },
  { id: 'movie', label: 'Movie', icon: Film, to: '/browse?type=movie' },
  { id: 'tv', label: 'TV', icon: Tv, to: '/browse?type=series' },
  { id: 'you', label: 'You', icon: UserIcon, to: '/profile' },
];

const matchesActive = (pathname, search, item) => {
  if (item.action) return false;
  const to = item.to;
  const [path, query] = to.split('?');
  if (query) {
    return pathname.startsWith(path) && search.includes(query);
  }
  if (path === '/home') return pathname === '/home' || pathname === '/';
  if (path === '/browse') return pathname.startsWith('/browse') && !search.includes('type=');
  if (path === '/profile') {
    return (
      pathname.startsWith('/profile') ||
      pathname.startsWith('/watchlist') ||
      pathname.startsWith('/slices')
    );
  }
  return pathname === path || pathname.startsWith(path + '/');
};

const KinoraSidebar = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const { balance, isActiveSubscriber } = useSliceBalance();
  const { unreadCount } = useViewerNotifications();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen]);

  const handleLogout = async () => {
    setMenuOpen(false);
    try {
      await logout();
      notificationService.authSuccess?.('logout');
      navigate('/');
    } catch (error) {
      notificationService.authError?.('logout', error);
    }
  };

  const sliceBalance = isAuthenticated ? (balance ?? 0) : null;
  const initial =
    user?.user_metadata?.full_name?.charAt(0)?.toUpperCase() ||
    user?.email?.charAt(0)?.toUpperCase() ||
    '?';
  const displayName =
    user?.user_metadata?.full_name ||
    user?.email?.split('@')[0] ||
    'Guest';

  const navItemClass = (isActive) =>
    cn(
      'flex items-center gap-3 rounded-lg p-2.5 text-[13px] font-medium transition-colors',
      'lg:justify-center lg:group-hover/sidebar:justify-start',
      isActive
        ? 'bg-[hsl(var(--kinora-card-hi))] text-foreground'
        : 'text-muted-foreground hover:bg-secondary/50 hover:text-foreground'
    );

  return (
    <aside
      className={cn(
        'group/sidebar sticky top-0 z-40 hidden h-screen shrink-0 flex-col gap-4 overflow-hidden',
        'bg-[hsl(var(--kinora-sidebar))] py-4',
        'w-[72px] transition-[width] duration-200 ease-out hover:w-[200px] lg:flex'
      )}
    >
      <Link
        to="/home"
        className="flex items-center gap-2.5 px-2 lg:justify-center lg:group-hover/sidebar:justify-start lg:group-hover/sidebar:px-3"
        title="Kinora"
      >
        <KinoraMark size={28} />
        <span className="hidden truncate text-lg font-semibold text-foreground lg:group-hover/sidebar:inline">
          Kinora
        </span>
      </Link>

      <nav className="flex flex-col gap-0.5 px-2">
        {NAV.map((item) => {
          const Icon = item.icon;
          const isActive = matchesActive(
            location.pathname,
            location.search,
            item
          );

          if (item.action === 'search') {
            return (
              <button
                key={item.id}
                type="button"
                title={item.label}
                onClick={() =>
                  navigate('/browse', { state: { focusSearch: true } })
                }
                className={navItemClass(false)}
              >
                <Icon className="h-5 w-5 shrink-0" />
                <span className="hidden truncate lg:group-hover/sidebar:inline">
                  {item.label}
                </span>
              </button>
            );
          }

          return (
            <Link
              key={item.id}
              to={item.to}
              title={item.label}
              className={navItemClass(isActive)}
            >
              <Icon
                className={cn(
                  'h-5 w-5 shrink-0',
                  isActive && 'text-primary'
                )}
              />
              <span className="hidden truncate lg:group-hover/sidebar:inline">
                {item.label}
              </span>
            </Link>
          );
        })}
      </nav>

      {isAuthenticated && (
        <Link
          to="/notifications"
          title="Notifications"
          className={cn(
            'mx-2 flex items-center gap-3 rounded-lg p-2.5 text-[13px] font-medium transition-colors',
            'lg:justify-center lg:group-hover/sidebar:justify-start',
            location.pathname === '/notifications'
              ? 'bg-[hsl(var(--kinora-card-hi))] text-foreground'
              : 'text-muted-foreground hover:bg-secondary/50 hover:text-foreground'
          )}
        >
          <div className="relative shrink-0">
            <Bell className="h-5 w-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[9px] font-bold text-primary-foreground leading-none">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </div>
          <span className="hidden truncate lg:group-hover/sidebar:inline">Notifications</span>
        </Link>
      )}

      <div className="flex-1" />

      {isActiveSubscriber && sliceBalance !== null && (
        <Link
          to="/slices"
          title="View slices"
          className={cn(
            'mx-2 flex items-center gap-2 rounded-lg px-2 py-2 transition-colors hover:bg-secondary/50',
            'lg:justify-center lg:group-hover/sidebar:justify-start'
          )}
        >
          <KinoraMark size={20} />
          <div className="hidden min-w-0 flex-1 lg:group-hover/sidebar:block">
            <div className="text-xs text-muted-foreground">Slices</div>
            <div className="text-sm font-semibold tabular-nums text-foreground">
              {sliceBalance}
            </div>
          </div>
          <ChevronRight className="hidden h-4 w-4 shrink-0 text-muted-foreground lg:group-hover/sidebar:block" />
        </Link>
      )}

      {isAuthenticated ? (
        <div
          ref={menuRef}
          className="relative mx-2 border-t border-border/30 pt-3"
        >
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            className={cn(
              'flex w-full items-center gap-2.5 rounded-lg p-1 text-left transition-opacity hover:opacity-80',
              'lg:justify-center lg:group-hover/sidebar:justify-start'
            )}
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/30 text-xs font-medium text-foreground">
              {initial}
            </div>
            <span className="hidden min-w-0 flex-1 truncate text-xs font-medium text-foreground lg:group-hover/sidebar:inline">
              {displayName}
            </span>
          </button>

          {menuOpen && (
            <div
              role="menu"
              className="absolute bottom-full left-0 right-0 z-50 mb-2 overflow-hidden rounded-xl border border-border/40 bg-popover shadow-lg"
            >
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                  navigate('/profile');
                }}
                className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-[13px] text-foreground hover:bg-secondary"
              >
                <UserIcon className="h-3.5 w-3.5" /> Profile
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                  navigate('/watchlist');
                }}
                className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-[13px] text-foreground hover:bg-secondary"
              >
                <Bookmark className="h-3.5 w-3.5" /> Watchlist
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                  navigate('/history');
                }}
                className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-[13px] text-foreground hover:bg-secondary"
              >
                <History className="h-3.5 w-3.5" /> History
              </button>
              <div className="h-px bg-border" />
              <button
                type="button"
                role="menuitem"
                onClick={handleLogout}
                className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-[13px] text-destructive hover:bg-secondary"
              >
                <LogOut className="h-3.5 w-3.5" /> Log out
              </button>
            </div>
          )}
        </div>
      ) : (
        <Link
          to="/auth"
          title="Sign in"
          className={cn(
            'mx-2 flex items-center justify-center rounded-md bg-primary py-2 text-[13px] font-medium text-primary-foreground transition-opacity hover:opacity-90',
            'lg:px-2 lg:group-hover/sidebar:px-4'
          )}
        >
          <UserIcon className="h-5 w-5 lg:group-hover/sidebar:hidden" />
          <span className="hidden lg:group-hover/sidebar:inline">Sign in</span>
        </Link>
      )}
    </aside>
  );
};

export default KinoraSidebar;
